(() => {
  /** @type {WeakMap<HTMLElement, AbortController>} */
  const tabAbortByRoot = new WeakMap();

  const T = window.theme?.translations ?? {};
  const labelLoading = () => T.collectionLoading || 'Loading…';

  /**
   * @param {string} html
   * @returns {Element | null}
   */
  const parseFragmentFromHTML = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.querySelector('[data-am-collection-tab-fragment]');
  };

  /**
   * @param {string} gridId
   */
  const abortPagination = (gridId) => {
    window.__amProductGridControllers?.[gridId]?.abort();
    delete window.__amProductGridControllers?.[gridId];
  };

  /**
   * @param {string} gridId
   */
  const initPagination = (gridId) => {
    if (typeof window.__amInitProductGridPagination === 'function') {
      window.__amInitProductGridPagination(gridId);
    }
  };

  /**
   * @param {HTMLElement} root
   * @param {HTMLButtonElement[]} buttons
   * @param {string} activeId
   */
  const setTabState = (root, buttons, activeId) => {
    buttons.forEach((btn) => {
      const match = btn.getAttribute('data-collection-tab-trigger') === activeId;
      btn.classList.toggle('is-active', match);
      btn.setAttribute('aria-selected', match ? 'true' : 'false');
      btn.tabIndex = match ? 0 : -1;
    });

    root.dataset.activeTab = activeId;
  };

  /**
   * @param {Element} fragment
   * @param {string} gridId
   */
  const rewriteGridIds = (fragment, gridId) => {
    const showMoreRoot = fragment.querySelector('[data-show-more-root]');
    if (showMoreRoot instanceof HTMLElement) {
      showMoreRoot.dataset.showMoreRoot = gridId;
    }

    fragment.querySelectorAll('.product-grid[data-section-id], .show-more-btn[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.dataset.sectionId = gridId;
      }
    });
  };

  /**
   * @param {HTMLElement} root
   * @param {HTMLButtonElement[]} buttons
   * @param {string} tabId
   * @param {{ force?: boolean }} [options]
   */
  const loadTab = async (root, buttons, tabId, { force = false } = {}) => {
    const btn = buttons.find((b) => b.getAttribute('data-collection-tab-trigger') === tabId);
    if (!btn) return;

    const collectionUrl = (btn.dataset.collectionUrl || '').trim();
    if (!collectionUrl) return;

    const activeTab = root.dataset.activeTab || '';
    const content = root.querySelector('[data-am-collection-tab-content]');
    const gridId = root.dataset.gridId || '';
    if (!content || !gridId) return;

    if (!force && activeTab === tabId && content.querySelector('[data-show-more-root]')) {
      setTabState(root, buttons, tabId);
      return;
    }

    abortPagination(gridId);
    setTabState(root, buttons, tabId);

    const fragmentView = root.dataset.fragmentView || 'tabs';
    const fetchUrl = new URL(collectionUrl, window.location.href);
    fetchUrl.searchParams.set('view', fragmentView);

    content.setAttribute('aria-busy', 'true');
    content.innerHTML = `<p class="am-collection-tabs__empty am-collection-tabs__loading">${labelLoading()}</p>`;

    tabAbortByRoot.get(root)?.abort();
    const tabAbort = new AbortController();
    tabAbortByRoot.set(root, tabAbort);

    try {
      const res = await fetch(fetchUrl.toString(), { signal: tabAbort.signal, credentials: 'same-origin' });
      if (!res.ok) {
        content.innerHTML = '<p class="am-collection-tabs__empty">Unable to load products.</p>';
        return;
      }

      const html = await res.text();
      if (root.dataset.activeTab !== tabId) return;

      const fragment = parseFragmentFromHTML(html);
      if (!fragment) {
        content.innerHTML = '<p class="am-collection-tabs__empty">Unable to load products.</p>';
        return;
      }

      rewriteGridIds(fragment, gridId);
      content.replaceChildren(...Array.from(fragment.childNodes));
      initPagination(gridId);
      window.__amPrimeQuickAddScripts?.();
      document.dispatchEvent(new CustomEvent('am:collection-tab:loaded', { detail: { root, tabId } }));
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (root.dataset.activeTab === tabId) {
        content.innerHTML = '<p class="am-collection-tabs__empty">Unable to load products.</p>';
      }
    } finally {
      if (root.dataset.activeTab === tabId) {
        content.removeAttribute('aria-busy');
      }
    }
  };

  /**
   * @param {HTMLElement} tablist
   */
  const initTablistDrag = (tablist) => {
    if (tablist.dataset.dragInit === 'true') return;
    tablist.dataset.dragInit = 'true';

    const canScroll = () => tablist.scrollWidth > tablist.clientWidth + 1;

    let isPointerDown = false;
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    /** @type {number | null} */
    let activePointerId = null;

    tablist.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse') return;
        if (!canScroll()) return;

        isPointerDown = true;
        isDragging = false;
        activePointerId = event.pointerId;
        startX = event.clientX;
        scrollLeft = tablist.scrollLeft;
      },
      { passive: true }
    );

    tablist.addEventListener(
      'pointermove',
      (event) => {
        if (!isPointerDown || event.pointerId !== activePointerId) return;

        const deltaX = event.clientX - startX;
        if (Math.abs(deltaX) < 5) return;

        if (!isDragging) {
          isDragging = true;
          tablist.classList.add('is-dragging');
          tablist.setPointerCapture(event.pointerId);
        }

        tablist.scrollLeft = scrollLeft - deltaX;
      },
      { passive: true }
    );

    /**
     * @param {PointerEvent} event
     */
    const endDrag = (event) => {
      if (!isPointerDown || event.pointerId !== activePointerId) return;

      const wasDragging = isDragging;
      isPointerDown = false;
      isDragging = false;
      activePointerId = null;
      tablist.classList.remove('is-dragging');

      if (tablist.hasPointerCapture(event.pointerId)) {
        tablist.releasePointerCapture(event.pointerId);
      }

      if (wasDragging) {
        tablist.dataset.suppressClick = 'true';
        window.setTimeout(() => {
          delete tablist.dataset.suppressClick;
        }, 0);
      }
    };

    tablist.addEventListener('pointerup', endDrag);
    tablist.addEventListener('pointercancel', endDrag);

    tablist.addEventListener(
      'click',
      (event) => {
        if (tablist.dataset.suppressClick === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );
  };

  /**
   * @param {HTMLElement} root
   */
  const initRoot = (root) => {
    if (root.dataset.amCollectionTabsInit === 'true') return;
    root.dataset.amCollectionTabsInit = 'true';

    const tablist = root.querySelector('.am-collection-tabs__tablist');
    const buttons = Array.from(root.querySelectorAll('[data-collection-tab-trigger]')).filter(
      (el) => el instanceof HTMLButtonElement
    );
    if (!buttons.length) return;

    if (tablist instanceof HTMLElement) {
      initTablistDrag(tablist);
    }

    const content = root.querySelector('[data-am-collection-tab-content]');
    const gridId = root.dataset.gridId || '';
    if (content && gridId && content.querySelector('[data-show-more-root]')) {
      initPagination(gridId);
    }

    const initial =
      root.dataset.activeTab ||
      buttons.find((b) => b.classList.contains('is-active'))?.getAttribute('data-collection-tab-trigger') ||
      buttons[0]?.getAttribute('data-collection-tab-trigger');

    if (initial) {
      setTabState(root, buttons, initial);
      if (content && !content.querySelector('[data-show-more-root]')) {
        const initialBtn = buttons.find((b) => b.getAttribute('data-collection-tab-trigger') === initial);
        if (initialBtn?.dataset.collectionUrl?.trim()) {
          loadTab(root, buttons, initial, { force: true });
        }
      }
    }

    buttons.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        const tabId = btn.getAttribute('data-collection-tab-trigger');
        if (tabId) loadTab(root, buttons, tabId);
      });

      btn.addEventListener('keydown', (e) => {
        if (!(e instanceof KeyboardEvent)) return;
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
        e.preventDefault();
        let next = index;
        if (e.key === 'ArrowRight') next = (index + 1) % buttons.length;
        else if (e.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = buttons.length - 1;
        const nextBtn = buttons[next];
        if (!nextBtn) return;
        nextBtn.focus();
        const tabId = nextBtn.getAttribute('data-collection-tab-trigger');
        if (tabId) loadTab(root, buttons, tabId);
      });
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-am-collection-tabs]').forEach((root) => {
      if (root instanceof HTMLElement) initRoot(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const root = target.querySelector('[data-am-collection-tabs]');
    if (root instanceof HTMLElement) initRoot(root);
  });
})();
